import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, isEqual, find } from 'lodash';
import { Image } from '@components/common';
import { OFFICER_STATUSES } from '@utils/constants';
import utils from '@utils';

class StatusFormatter extends Component {
	shouldComponentUpdate({
		row : {
			Jobs,
			ApplicantsNeeded,
			ApplicantsAssigned,
			AverageRate,
		},
	}) {
		const lastProps = this.props;

		return (
			!isEqual(Jobs, lastProps.row.Jobs)
			|| !isEqual(ApplicantsNeeded, lastProps.row.ApplicantsNeeded)
			|| !isEqual(ApplicantsAssigned, lastProps.row.ApplicantsAssigned)
			|| !isEqual(AverageRate, lastProps.row.AverageRate)
		);
	}

	getTagClass = (officer) => {
		const officerStatusType = get(officer, 'OfficerStatus', 0);
		const status = find(OFFICER_STATUSES, ['type', officerStatusType]) || OFFICER_STATUSES.none;

		return `tag-${status.color}`;
	}

	getStatusDescription = (officer) => {
		const officerStatusType = get(officer, 'OfficerStatus', 0);
		const status = find(OFFICER_STATUSES, ['type', officerStatusType]) || OFFICER_STATUSES.none;

		return status.description;
	}

	renderStaffedStatus = (padded = false) => {
		const { row : { ApplicantsNeeded, ApplicantsAssigned }, viewType } = this.props;
		let message;

		if (ApplicantsAssigned === 0) message = 'Unstaffed';
		else if ((ApplicantsAssigned === ApplicantsNeeded) && viewType === 'upcoming') message = 'Staffed';
		else return undefined;

		return (
			<div
				className={
					classNames('tag',
						{
							'margin-top-25' : padded,
							'tag-danger'    : ApplicantsAssigned === 0,
							'tag-success'   : (ApplicantsAssigned === ApplicantsNeeded) && viewType === 'upcoming',
						})
				}
			>
				{ message }
			</div>
		);
	}

	renderPositionSummary = () => {
		const {
			row : {
				Jobs,
				ApplicantsNeeded,
				ApplicantsAssigned,
				MinRate,
				MaxRate,
				RequirePrepayment,
			},
		} = this.props;

		const jobsCount = Jobs.length;
		const jobRate = MinRate === MaxRate
			? `${utils.currencyFormatter(MinRate, false)}/hour`
			: `${utils.currencyFormatter(MinRate, false)} - ${utils.currencyFormatter(MaxRate, false)}/hour`;

		return (
			<div className="position-summary">
				<div>
					<span>
						{ `${jobsCount} position${jobsCount !== 1 ? 's' : ''}` }
					</span>
					<span className="dot" />
					<span>
						{ `${ApplicantsAssigned}/${ApplicantsNeeded} staffed` }
					</span>
				</div>
				<div className="rate">
					<span>{`Officer Rate: ${jobRate}`}</span>
				</div>
				{ this.renderStaffedStatus(true) }
				<If condition={RequirePrepayment}>
					<div
						className="tag tag-success text-uppercase"
						title="This job has been prepaid, no overtime hours are allowed."
					>
						Prepaid
					</div>
				</If>
			</div>
		);
	}

	render() {
		const {
			row : {
				Jobs,
				ApplicantsNeeded,
				ApplicantsAssigned,
				AverageRate,
				RequirePrepayment,
			},
		} = this.props;
		const assignedOfficers = Jobs
			.filter((item) => item.AssignedOfficers.length > 0)
			.map((item) => item.AssignedOfficers)
			.reduce((acc, item) => [...acc, ...item], [])
			.map((item) => ({
				...item.OfficerProfile,
				OfficerName   : `${item.OfficerProfile.FirstName} ${item.OfficerProfile.LastName}`,
				OfficerStatus : item.OfficerStatus,
			}));

		const externalOfficers = Jobs
			.filter((item) => item.ExternalOfficers.length > 0)
			.map((item) => item.ExternalOfficers)
			.reduce((acc, item) => [...acc, ...item], []);

		const allOfficers = [...assignedOfficers, ...externalOfficers];
		const officers = allOfficers.length > 3 ? allOfficers.slice(0, 3) : allOfficers;
		const allOfficersCount = allOfficers.length;
		const firstOfficer = get(allOfficers, '0', {});

		return (
			<div className="status-formatter">
				<Choose>
					<When condition={ApplicantsAssigned === 1 && ApplicantsNeeded === 1}>
						<div className="one-user">
							<div className="image-container">
								<Image
									className="user-image"
									src={get(firstOfficer, 'ContactPhotoUrl', '')}
									fallbackSrc="avatar"
								/>
							</div>
							<div className="name-container">
								<h5>{ get(firstOfficer, 'OfficerName', '') }</h5>
								<div>
									<span>
										{ firstOfficer.CreateDateUTC ? 'Non-RollKall Officer' : get(firstOfficer, 'OfficerRank', 'No Rank') }
									</span>
								</div>
								<div>
									<span>Rate:</span>
									<span>
										$
										{ AverageRate }
										/hour
									</span>
								</div>
							</div>
						</div>
						<div>
							<Choose>
								<When condition={firstOfficer.OfficerStatus === OFFICER_STATUSES.none.type}>
									{ this.renderStaffedStatus() }
								</When>
								<Otherwise>
									<div className={classNames('tag', this.getTagClass(firstOfficer))}>
										{ this.getStatusDescription(firstOfficer) }
									</div>
								</Otherwise>
							</Choose>
							<If condition={RequirePrepayment}>
								<div
									className="tag tag-success text-uppercase"
									title="This job has been prepaid, no overtime hours are allowed."
								>
									Prepaid
								</div>
							</If>
						</div>
					</When>
					<When condition={ApplicantsAssigned > 0 && ApplicantsNeeded > 1}>
						<div className="images-row">
							<For
								each="item"
								index="index"
								of={officers}
							>
								<div
									key={`officer-thumb-${index}-${item.UserUID}`}
									className={classNames('image-thumb', `z-index-${index}`, `right10x-${index}`)}
									title={get(item, 'OfficerName', '')}
								>
									<Image
										className="user-image"
										src={item.ContactPhotoUrl}
										fallbackSrc="avatar"
									/>
								</div>
							</For>
							<If condition={allOfficersCount > 3}>
								<div className="number-thumb">
									{`+${allOfficersCount - 3}`}
								</div>
							</If>
						</div>
					</When>
				</Choose>
				<If
					condition={(
						(ApplicantsAssigned === 0 && ApplicantsNeeded <= 1)
						|| (ApplicantsAssigned >= 0 && ApplicantsNeeded > 1)
					)}
				>
					{ this.renderPositionSummary() }
				</If>
			</div>
		);
	}
}

StatusFormatter.propTypes = {
	viewType : PropTypes.string,
	row      : PropTypes.object.isRequired,
};

StatusFormatter.defaultProps = {
	viewType : '',
};

export default StatusFormatter;
